import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LocationCard from './components/LocationCard';
import LocationMap from './components/LocationMap';
import LocationSearch from './components/LocationSearch';
import LocationStats from './components/LocationStats';

const CollectionPoints = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    services: 'all',
    distance: 'all'
  });

  // Mock data for collection points - Only one active location
  const mockLocations = [
    {
      id: 1,
      name: "Maayo Fabric Care",
      address: "30/1, Tank Street, Velappanaickenpudur, Chinnavedampatti, Coimbatore – 641049",
      locationLandmark: "Thudiyalur to Saravampatti Road, Sunnambu Kalavai bus stop",
      coordinates: { lat: 11.0829639, lng: 76.9456458 },
      directionsUrl: "https://www.google.com/maps/dir/?api=1&destination=Maayo+Laundry+and+Dry+cleaning",
      mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31323.261337118307!2d76.94564580917357!3d11.082963917948165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f7ad62a82c93%3A0x9b9bb2cd3fe1f500!2sMaayo%20Laundry%20and%20Dry%20cleaning!5e0!3m2!1sen!2sus!4v1772088152644!5m2!1sen!2sus",
      phone: "+91 96000 44004",
      hours: "8:00 AM - 9:00 PM",
      status: "Open",
      distance: "1.2 km",
      services: ["Dry Cleaning", "Laundry Wash & Fold", "Laundry Wash & Iron", "Steam Press", "Toys Cleaning", "Shoe Care", "Leather Care", "Carpet Cleaning", "Curtain Cleaning"],
      // featured: true,
      hasPickup: true,
      has24Hour: false,
      hasExpressService: true,
      currentCapacity: 45,
      specialInstructions: "Free pickup available for orders above ₹500. Located near Sunnambu Kalavai bus stop on Thudiyalur to Saravampatti Road."
    }
    /* --- OTHER LOCATIONS (2-6) - Commented ---
    ,{
      id: 2,
      name: "Maayo Fabric Care - Gandhipuram",
      address: "456, Cross Cut Road, Gandhipuram, Coimbatore - 641012",
      coordinates: { lat: 11.0175, lng: 76.9675 },
      phone: "+91 98765 43211",
      hours: "6:00 AM - 11:00 PM",
      status: "Open",
      distance: "2.8 km",
      services: ["Wash & Fold", "Dry Clean", "Iron & Press"],
      featured: true,
      hasPickup: true,
      has24Hour: false,
      hasExpressService: false,
      currentCapacity: 72,
      specialInstructions: "Located near Big Bazaar. Ample parking space available."
    },
    {
      id: 3,
      name: "Maayo Fabric Care - Peelamedu",
      address: "789, Avinashi Road, Peelamedu, Coimbatore - 641004",
      coordinates: { lat: 11.0296, lng: 76.9270 },
      phone: "+91 98765 43212",
      hours: "24 Hours",
      status: "Open",
      distance: "3.5 km",
      services: ["Wash & Fold", "Iron & Press", "Pickup & Delivery"],
      featured: false,
      hasPickup: true,
      has24Hour: true,
      hasExpressService: true,
      currentCapacity: 28,
      specialInstructions: "24/7 service available. Self-service kiosks for after-hours pickup."
    },
    {
      id: 4,
      name: "Maayo Fabric Care - Saibaba Colony",
      address: "321, Mettupalayam Road, Saibaba Colony, Coimbatore - 641011",
      coordinates: { lat: 11.0510, lng: 76.9339 },
      phone: "+91 98765 43213",
      hours: "7:00 AM - 9:00 PM",
      status: "Open",
      distance: "4.1 km",
      services: ["Wash & Fold", "Dry Clean"],
      featured: false,
      hasPickup: false,
      has24Hour: false,
      hasExpressService: false,
      currentCapacity: 85,
      specialInstructions: "Drop-off only location. No pickup service available."
    },
    {
      id: 5,
      name: "Maayo Fabric Care - Singanallur",
      address: "654, Trichy Road, Singanallur, Coimbatore - 641005",
      coordinates: { lat: 10.9435, lng: 76.9847 },
      phone: "+91 98765 43214",
      hours: "8:00 AM - 8:00 PM",
      status: "Closed",
      distance: "5.7 km",
      services: ["Wash & Fold", "Iron & Press", "Pickup & Delivery"],
      featured: false,
      hasPickup: true,
      has24Hour: false,
      hasExpressService: false,
      currentCapacity: 15,
      specialInstructions: "Temporarily closed for maintenance. Will reopen tomorrow at 8 AM."
    },
    {
      id: 6,
      name: "Maayo Fabric Care - Race Course",
      address: "987, Race Course Road, Race Course, Coimbatore - 641018",
      coordinates: { lat: 11.0031, lng: 76.9597 },
      phone: "+91 98765 43215",
      hours: "6:30 AM - 10:30 PM",
      status: "Open",
      distance: "2.3 km",
      services: ["Wash & Fold", "Dry Clean", "Iron & Press", "Pickup & Delivery"],
      featured: true,
      hasPickup: true,
      has24Hour: false,
      hasExpressService: true,
      currentCapacity: 58,
      specialInstructions: "Premium location with leather and suede cleaning services."
    }
    --- End OTHER LOCATIONS --- */
  ];

  useEffect(() => {
    applyFilters();
  }, [searchQuery, filters]);

  const applyFilters = () => {
    let filtered = mockLocations;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(location =>
        location?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        location?.address?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(location =>
        location?.status?.toLowerCase() === filters?.status
      );
    }

    // Apply services filter
    if (filters?.services !== 'all') {
      const serviceMap = {
        'wash': 'Wash & Fold',
        'dry-clean': 'Dry Clean',
        'iron': 'Iron & Press',
        'pickup': 'Pickup & Delivery'
      };
      filtered = filtered?.filter(location =>
        location?.services?.includes(serviceMap?.[filters?.services])
      );
    }

    // Apply distance filter
    if (filters?.distance !== 'all') {
      const maxDistance = parseFloat(filters?.distance?.replace('km', ''));
      filtered = filtered?.filter(location =>
        parseFloat(location?.distance) <= maxDistance
      );
    }

    setFilteredLocations(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleViewToggle = (view) => {
    setCurrentView(view);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    if (currentView === 'list') {
      setCurrentView('map');
    }
  };

  const handleGetDirections = (location) => {
    const url = location?.directionsUrl || `https://www.google.com/maps/dir/?api=1&destination=${location?.coordinates?.lat},${location?.coordinates?.lng}`;
    window.open(url, '_blank');
  };

  const handleCallLocation = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, rotateX: -15 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const statsVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 0.3
      }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <motion.main
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="pt-8 pb-16"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div
              className="flex items-center justify-center mb-4"
              initial={{ scale: 0, rotate: -180 }}
              whileInView={{ scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Icon name="MapPin" size={32} className="text-primary" />
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl lg:text-5xl font-bold text-foreground mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Collection Points
            </motion.h1>
            <motion.p
              className="text-xl text-muted-foreground max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Find convenient pickup and drop-off locations throughout Coimbatore.
              Choose from our network of premium collection points for easy access to our services.
            </motion.p>
          </motion.div>

          {/* Location Stats */}
          <motion.div
            variants={statsVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <LocationStats locations={mockLocations} />
          </motion.div>

          {/* Search and Filters */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <LocationSearch
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              onViewToggle={handleViewToggle}
              currentView={currentView}
              totalLocations={filteredLocations?.length}
            />
          </motion.div>

          {/* Main Content */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {currentView === 'list' ? (
              <div className="space-y-8">
                {filteredLocations?.length > 0 ? (
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.2,
                          delayChildren: 0.1
                        }
                      }
                    }}
                  >
                    {filteredLocations?.slice(0, 6)?.map((location, index) => (
                      <motion.div
                        key={location?.id}
                        variants={cardVariants}
                        whileHover={{
                          scale: 1.02,
                          y: -5,
                          transition: { duration: 0.2 }
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <LocationCard
                          location={location}
                          index={index}
                          onGetDirections={handleGetDirections}
                          onCallLocation={handleCallLocation}
                          isSelected={selectedLocation?.id === location?.id}
                          onSelect={handleLocationSelect}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                ) : (
                  <div className="bg-card rounded-xl p-12 text-center shadow-elevation-1">
                    <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      No locations found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try adjusting your search criteria or filters to find collection points.
                    </p>
                    <Button
                      variant="outline"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={() => {
                        setSearchQuery('');
                        setFilters({
                          status: 'all',
                          services: 'all',
                          distance: 'all'
                        });
                      }}
                    >
                      Reset Filters
                    </Button>
                  </div>
                )}

                {/* Quick Actions and Service Hours */}
                {filteredLocations?.length > 0 && (
                  <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-6"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.3,
                          delayChildren: 0.2
                        }
                      }
                    }}
                  >
                    {/* Quick Actions */}
                    <motion.div
                      className="bg-card rounded-xl p-6 shadow-elevation-1"
                      variants={cardVariants}
                      whileHover={{
                        scale: 1.02,
                        y: -3,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <h3 className="text-lg font-semibold text-foreground mb-4">
                        Quick Actions
                      </h3>
                      <div className="space-y-3">
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            variant="outline"
                            fullWidth
                            iconName="Calendar"
                            iconPosition="left"
                            onClick={() => window.location.href = '/pickup-scheduling'}
                          >
                            Schedule Pickup
                          </Button>
                        </motion.div>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            variant="outline"
                            fullWidth
                            iconName="Phone"
                            iconPosition="left"
                            onClick={() => window.location.href = '/contact-us'}
                          >
                            Call Support
                          </Button>
                        </motion.div>
                        {/* <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            variant="outline"
                            fullWidth
                            iconName="MessageCircle"
                            iconPosition="left"
                            onClick={() => window.location.href = '/contact-us'}
                          >
                            Live Chat
                          </Button>
                        </motion.div> */}
                      </div>
                    </motion.div>

                    {/* Service Hours */}
                    <motion.div
                      className="bg-card rounded-xl p-6 shadow-elevation-1"
                      variants={cardVariants}
                      whileHover={{
                        scale: 1.02,
                        y: -3,
                        transition: { duration: 0.2 }
                      }}
                    >
                      <h3 className="text-lg font-semibold text-foreground mb-4">
                        Business Hours
                      </h3>
                      <div className="space-y-3">
                        <motion.div
                          className="flex justify-between items-center"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                        >
                          <span className="text-sm text-muted-foreground">Monday - Saturday</span>
                          <span className="text-sm font-medium text-foreground">8:00 AM - 9:00 PM</span>
                        </motion.div>
                        <motion.div
                          className="flex justify-between items-center"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                        >
                          <span className="text-sm text-muted-foreground">Sunday</span>
                          <span className="text-sm font-medium text-foreground">8:00 AM - 9:00 PM (Pickup only)</span>
                        </motion.div>
                        <motion.div
                          className="pt-3 border-t border-border"
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                        >
                          <div className="flex items-center text-sm text-success">
                            <Icon name="Truck" size={16} className="mr-2" />
                            <span>Pickup Available: 7:00 AM - 9:00 PM</span>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="h-96 lg:h-[600px]">
                <LocationMap
                  selectedLocation={selectedLocation}
                  locations={filteredLocations}
                  onLocationSelect={handleLocationSelect}
                />
              </div>
            )}
          </motion.div>

          {/* Call to Action */}
          <motion.div
            variants={ctaVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-16 bg-primary rounded-2xl p-8 lg:p-12 text-center"
            whileHover={{
              scale: 1.01,
              transition: { duration: 0.3 }
            }}
          >
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Need Help Finding a Location?
            </motion.h2>
            <motion.p
              className="text-xl text-white mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Our customer support team is here to help you find the most convenient collection point
              and schedule your pickup service.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="Phone"
                  iconPosition="left"
                  onClick={() => window.open('tel:+919600044004')}
                  className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                >
                  Call Us Now
                </Button>
              </motion.div>
              {/* <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="outline"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10"
                >
                  Start Live Chat
                </Button>
              </motion.div> */}
            </motion.div>
          </motion.div>
        </div>
      </motion.main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CollectionPoints;